import i18n from 'i18next';
import { logger } from './logger';

// Note: Using backend set-cookie for gid management

class GuestSessionManager {
  private sessionId: string | null = null;
  private readonly STORAGE_KEY = 'guest_session_gid';

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    // First try to load from localStorage
    const storedGid = localStorage.getItem(this.STORAGE_KEY);
    if (storedGid) {
      this.sessionId = storedGid;
      logger.log('🔍 GuestSession loaded from localStorage:', storedGid);
      return;
    }

    // Fallback to cookie if localStorage is empty
    const gidCookie = this.getCookieValue('gid');
    if (gidCookie) {
      this.sessionId = gidCookie;
      // Save to localStorage for future use
      localStorage.setItem(this.STORAGE_KEY, gidCookie);
      logger.log('🔍 GuestSession loaded from cookie and saved to localStorage:', gidCookie);
    }
  }

  private saveToStorage(gid: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, gid);
      logger.log('🔍 GuestSession saved to localStorage:', gid);
    } catch (error) {
      logger.warn('🔍 Failed to save gid to localStorage:', error);
    }
  }

  private getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  async getSession(): Promise<string> {
    logger.log('🔍 GuestSessionManager getSession called');

    // Refresh from storage in case it was updated by backend
    this.loadFromStorage();

    if (this.sessionId) {
      logger.log('🔍 Returning existing session from storage:', this.sessionId);
      return this.sessionId;
    }

    // Generate new gid if none exists
    const newGid = this.generateGuestId();
    logger.log('🔍 Generated new gid:', newGid);

    // Save to localStorage
    this.setSessionId(newGid);

    return newGid;
  }

  getCurrentSessionId(): string | null {
    // Always check storage for latest value
    this.loadFromStorage();
    return this.sessionId;
  }

  isSessionValid(): boolean {
    // Always check storage for latest value
    this.loadFromStorage();
    return this.sessionId !== null;
  }

  async clearGuestSession(): Promise<void> {
    // Clear both localStorage and memory
    this.sessionId = null;
    localStorage.removeItem(this.STORAGE_KEY);
    logger.log('🔍 GuestSession cleared from localStorage and memory');
  }

  /**
   * Headers the backend guest middleware actually reads.
   * Canonical: `X-Guest-Id`. `gid` is kept as a legacy alias.
   */
  guestIdHeaders(guestId: string): Record<string, string> {
    return {
      'X-Guest-Id': guestId,
      gid: guestId,
    };
  }

  getGuestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Accept-Language': i18n.language || 'en',
    };

    if (this.sessionId) {
      Object.assign(headers, this.guestIdHeaders(this.sessionId));
    }

    return headers;
  }

  // Method to refresh session from storage (useful after API calls)
  refreshFromStorage(): void {
    this.loadFromStorage();
  }

  // Keep backward compatibility
  refreshFromCookie(): void {
    this.refreshFromStorage();
  }

  // Method to directly set session ID and save to localStorage
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.saveToStorage(sessionId);
    logger.log('🔍 Session ID set directly and saved to localStorage:', sessionId);
  }

  // Generate a new guest ID
  private generateGuestId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `guest_${timestamp}_${random}`;
  }
}

// Create and export a singleton instance
export const guestSessionManager = new GuestSessionManager();
export default guestSessionManager;
